import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowDownCircle, ArrowUpCircle, CheckCircle,
  IndianRupee, TrendingUp, ShieldCheck,
  Building2, CreditCard, QrCode, Copy, CheckCheck,
  ChevronLeft, Smartphone, Hash, Upload, StickyNote, X,
} from 'lucide-react';
import { investmentAPI, getCompanyBankAccount } from '../api/investment';
import { Card } from '../components/ui/Card';
import Button from '../components/ui/Button';
import StatusBadge from '../components/ui/StatusBadge';
import PageHeader from '../components/ui/PageHeader';

const PRESET_AMOUNTS   = [155, 210, 510, 1030, 1540, 2060];
const MANUAL_THRESHOLD = 2500;   // amounts above this → bank transfer, not Razorpay

const fmt = (n) => Number(n).toLocaleString('en-IN');

const CHIP_INACTIVE = { background: 'rgba(212,160,23,0.1)', color: '#f0c040', border: '1px solid rgba(212,160,23,0.3)' };
const CHIP_ACTIVE   = { background: 'linear-gradient(135deg, #d4a017, #f0c040)', color: '#fff', boxShadow: '0 2px 10px rgba(212,160,23,0.35)' };
const TAB_INACTIVE  = { color: 'rgba(255,255,255,0.5)' };
const TAB_ACTIVE    = { background: 'linear-gradient(135deg, #d4a017, #f0c040)', color: '#fff', boxShadow: '0 2px 10px rgba(212,160,23,0.3)' };

// /** Dynamically load the Razorpay checkout script — DISABLED */
// function loadRazorpayScript() {
//   return new Promise((resolve) => {
//     if (window.Razorpay) { resolve(true); return; }
//     const script = document.createElement('script');
//     script.src     = 'https://checkout.razorpay.com/v1/checkout.js';
//     script.onload  = () => resolve(true);
//     script.onerror = () => resolve(false);
//     document.body.appendChild(script);
//   });
// }

// ── Copy-to-clipboard helper ───────────────────────────────────────────────
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <button onClick={copy} className="flex-shrink-0 ml-1" style={{ color: copied ? '#6ee7b7' : 'rgba(255,255,255,0.35)' }}>
      {copied ? <CheckCheck size={13} /> : <Copy size={13} />}
    </button>
  );
}

// ── Bank detail row ────────────────────────────────────────────────────────
function BankRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between gap-2 py-2 border-b"
         style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
      <p className="text-[11px] font-medium" style={{ color: 'rgba(255,255,255,0.45)' }}>{label}</p>
      <div className="flex items-center gap-1 min-w-0">
        <p className="text-xs font-semibold text-white text-right truncate">{value}</p>
        <CopyButton text={value} />
      </div>
    </div>
  );
}

// ── Bank details view ──────────────────────────────────────────────────────
function BankDetailsView({ amount, bankData, onBack, onConfirm, submitting, error }) {
  const hasBank = bankData?.account_number;
  const hasUpi  = bankData?.upi_id || bankData?.upi_number;

  const [refNumber,         setRefNumber]         = useState('');
  const [note,              setNote]              = useState('');
  const [screenshot,        setScreenshot]        = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [fileError,         setFileError]         = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      setFileError('Image must be smaller than 15 MB.');
      e.target.value = '';
      return;
    }
    setFileError('');
    setScreenshot(file);
    setScreenshotPreview(URL.createObjectURL(file));
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    setScreenshotPreview(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -24 }}
      className="flex flex-col gap-4"
    >
      {/* Amount to transfer */}
      <div
        className="rounded-2xl px-5 py-4 flex items-center justify-between"
        style={{ background: 'linear-gradient(135deg, #d4a017 0%, #f0c040 100%)' }}
      >
        <div>
          <p className="text-xs font-medium text-amber-100 mb-0.5">Amount to Transfer</p>
          <p className="text-2xl font-serif font-bold text-white">₹{fmt(amount)}</p>
        </div>
        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
          <Building2 size={20} color="white" />
        </div>
      </div>

      {error && (
        <div className="rounded-xl border px-4 py-3 text-sm"
             style={{ background: 'rgba(239,68,68,0.15)', borderColor: 'rgba(239,68,68,0.3)', color: '#fca5a5' }}>
          {error}
        </div>
      )}

      {/* Bank account details */}
      {hasBank && (
        <Card>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                 style={{ background: 'rgba(20,72,196,0.2)' }}>
              <Building2 size={14} color="#93c5fd" />
            </div>
            <p className="text-xs font-bold text-white uppercase tracking-wide">Bank Transfer</p>
          </div>

          <BankRow label="Account Holder" value={bankData.account_holder_name} />
          <BankRow label="Bank Name"      value={bankData.bank_name} />
          <BankRow label="Account No."   value={bankData.account_number} />
          <BankRow label="IFSC Code"     value={bankData.ifsc} />
          <BankRow label="Branch"        value={bankData.branch} />
        </Card>
      )}

      {/* UPI details */}
      {hasUpi && (
        <Card>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                 style={{ background: 'rgba(5,150,105,0.18)' }}>
              <Smartphone size={14} color="#6ee7b7" />
            </div>
            <p className="text-xs font-bold text-white uppercase tracking-wide">UPI Payment</p>
          </div>

          {bankData.upi_id && <BankRow label="UPI ID" value={bankData.upi_id} />}
          {bankData.upi_number && <BankRow label="UPI Number" value={bankData.upi_number} />}

          {/* QR Code */}
          {bankData.qr_code_url && (
            <div className="flex flex-col items-center mt-3 pt-3 border-t"
                 style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
              <p className="text-[10px] mb-2" style={{ color: 'rgba(255,255,255,0.35)' }}>
                Scan QR to pay
              </p>
              <img
                src={bankData.qr_code_url}
                alt="UPI QR Code"
                className="w-36 h-36 rounded-xl object-contain"
                style={{ background: 'white', padding: '6px' }}
              />
            </div>
          )}
        </Card>
      )}

      {/* Instruction note */}
      <div className="rounded-xl px-4 py-3 flex gap-3"
           style={{ background: 'rgba(212,160,23,0.1)', border: '1px solid rgba(212,160,23,0.25)' }}>
        <CreditCard size={14} color="#f0c040" className="flex-shrink-0 mt-0.5" />
        <p className="text-[11px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
          Transfer exactly <span style={{ color: '#f0c040', fontWeight: 700 }}>₹{fmt(amount)}</span> to
          the account above. After completing the transfer, fill the details below and submit.
          Your investment will be activated once the admin verifies the payment.
        </p>
      </div>

      {/* ── Optional transfer details ────────────────────────────────────── */}
      <Card>
        <p className="text-xs font-bold text-white uppercase tracking-wide mb-4">
          Transfer Details <span className="normal-case font-normal text-[11px]" style={{ color: 'rgba(255,255,255,0.35)' }}>(optional)</span>
        </p>

        {/* Reference / UTR number */}
        <div className="mb-4">
          <label className="flex items-center gap-1.5 text-[11px] font-medium mb-2"
                 style={{ color: 'rgba(255,255,255,0.5)' }}>
            <Hash size={11} />
            Reference / UTR Number
          </label>
          <input
            type="text"
            placeholder="e.g. 123456789012"
            value={refNumber}
            onChange={(e) => setRefNumber(e.target.value)}
            className="input-field w-full"
          />
        </div>

        {/* Screenshot upload */}
        <div className="mb-4">
          <label className="flex items-center gap-1.5 text-[11px] font-medium mb-2"
                 style={{ color: 'rgba(255,255,255,0.5)' }}>
            <Upload size={11} />
            Payment Screenshot
          </label>

          {screenshotPreview ? (
            <div className="relative inline-block">
              <img
                src={screenshotPreview}
                alt="Payment screenshot"
                className="w-full max-h-48 rounded-xl object-contain"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
              <button
                onClick={removeScreenshot}
                className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                <X size={12} color="white" />
              </button>
              <p className="text-[10px] mt-1.5 truncate" style={{ color: 'rgba(255,255,255,0.35)' }}>
                {screenshot?.name}
              </p>
            </div>
          ) : (
            <label
              className="flex flex-col items-center justify-center gap-2 rounded-xl py-6 cursor-pointer transition-all active:scale-[0.98]"
              style={{ border: '1.5px dashed rgba(212,160,23,0.35)', background: 'rgba(212,160,23,0.05)' }}
            >
              <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                   style={{ background: 'rgba(212,160,23,0.15)' }}>
                <Upload size={16} color="#f0c040" />
              </div>
              <p className="text-xs font-medium" style={{ color: '#f0c040' }}>Tap to upload screenshot</p>
              <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>JPG, PNG, WEBP — max 15 MB</p>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </label>
          )}
          {fileError && (
            <p className="text-[11px] mt-1" style={{ color: '#fca5a5' }}>{fileError}</p>
          )}
        </div>

        {/* Note */}
        <div>
          <label className="flex items-center gap-1.5 text-[11px] font-medium mb-2"
                 style={{ color: 'rgba(255,255,255,0.5)' }}>
            <StickyNote size={11} />
            Note
          </label>
          <textarea
            placeholder="Any additional information for the admin…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="input-field w-full resize-none"
          />
        </div>
      </Card>

      {/* Action buttons */}
      <div className="flex gap-3">
        <button
          onClick={onBack}
          disabled={submitting}
          className="flex items-center justify-center gap-1.5 rounded-2xl py-3.5 px-4 text-sm font-semibold transition-all active:scale-95 disabled:opacity-40"
          style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)' }}
        >
          <ChevronLeft size={15} />
          Back
        </button>

        <Button
          fullWidth
          loading={submitting}
          disabled={submitting}
          onClick={() => onConfirm({ refNumber, screenshot, note })}
        >
          {submitting ? 'Submitting…' : "I've Transferred — Submit"}
        </Button>
      </div>
    </motion.div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function InvestPage() {
  const [tab, setTab] = useState('invest');

  const [selected, setSelected]   = useState(null);
  const [customAmt, setCustomAmt] = useState('');
  const [apiError, setApiError]   = useState('');

  // Razorpay flow
  const [investing, setInvesting] = useState(false);

  // Manual bank-transfer flow (amount > MANUAL_THRESHOLD)
  const [showBank, setShowBank]     = useState(false);
  const [bankData, setBankData]     = useState(null);
  const [loadingBank, setLoadingBank] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Success state
  const [lastInvest, setLastInvest] = useState(null);

  // History
  const [history, setHistory]         = useState([]);
  const [pagination, setPagination]   = useState(null);
  const [loadingHistory, setLh]       = useState(false);
  const [historyPage, setHistoryPage] = useState(1);

  const [balance, setBalance] = useState(null);

  useEffect(() => {
    investmentAPI.getBalance()
      .then((r) => setBalance(r.data.data))
      .catch(() => {});
  }, []);

  const loadHistory = useCallback((page = 1) => {
    setLh(true);
    investmentAPI.getHistory({ page, per_page: 10 })
      .then((r) => {
        setHistory(r.data.data.history    ?? []);
        setPagination(r.data.data.pagination ?? null);
        setHistoryPage(page);
      })
      .catch(() => {})
      .finally(() => setLh(false));
  }, []);

  useEffect(() => {
    if (tab === 'history') loadHistory(1);
  }, [tab, loadHistory]);

  const resolvedAmount = selected === 'custom'
    ? parseFloat(customAmt) || 0
    : selected ?? 0;

  const isManual = true; // Payment gateway disabled — all amounts use manual bank transfer

  // ── Handle invest click ────────────────────────────────────────────────
  const handleInvest = async () => {
    if (!resolvedAmount || resolvedAmount < 1) {
      setApiError('Please select or enter a valid amount.');
      return;
    }
    setApiError('');

    if (isManual) {
      // Fetch company bank account and show transfer details
      setLoadingBank(true);
      try {
        const res = await getCompanyBankAccount();
        setBankData(res.data.data);
        setShowBank(true);
      } catch {
        setApiError('Failed to load bank details. Please try again.');
      } finally {
        setLoadingBank(false);
      }
      return;
    }

    // ── Razorpay flow DISABLED — all amounts now use manual bank transfer ──
    // setInvesting(true);
    // try {
    //   const orderRes = await investmentAPI.createOrder({ amount: resolvedAmount });
    //   const { order_id, amount: rzpAmount, currency, key_id } = orderRes.data.data;
    //
    //   const loaded = await loadRazorpayScript();
    //   if (!loaded) {
    //     setApiError('Failed to load payment gateway. Please check your connection and try again.');
    //     setInvesting(false);
    //     return;
    //   }
    //
    //   const options = {
    //     key:         key_id,
    //     amount:      rzpAmount,
    //     currency:    currency,
    //     name:        'Shine Jewellery',
    //     description: 'Gold Investment',
    //     order_id:    order_id,
    //     theme:       { color: '#d4a017' },
    //
    //     handler: async (response) => {
    //       try {
    //         const verifyRes = await investmentAPI.verifyPayment({
    //           razorpay_order_id:   response.razorpay_order_id,
    //           razorpay_payment_id: response.razorpay_payment_id,
    //           razorpay_signature:  response.razorpay_signature,
    //           amount:              resolvedAmount,
    //         });
    //         setLastInvest(verifyRes.data.data);
    //         setSelected(null);
    //         setCustomAmt('');
    //         investmentAPI.getBalance().then((r) => setBalance(r.data.data)).catch(() => {});
    //       } catch (err) {
    //         setApiError(
    //           err.response?.data?.message || 'Payment verification failed. Please contact support.'
    //         );
    //       } finally {
    //         setInvesting(false);
    //       }
    //     },
    //
    //     modal: { ondismiss: () => setInvesting(false) },
    //   };
    //
    //   const rzp = new window.Razorpay(options);
    //   rzp.on('payment.failed', (response) => {
    //     setApiError(response.error?.description || 'Payment failed. Please try again.');
    //     setInvesting(false);
    //   });
    //   rzp.open();
    // } catch (err) {
    //   setApiError(
    //     err.response?.data?.errors?.amount?.[0] ||
    //     err.response?.data?.message ||
    //     'Failed to initiate payment. Please try again.'
    //   );
    //   setInvesting(false);
    // }
  };

  // ── Handle manual transfer confirmation ────────────────────────────────
  const handleManualConfirm = async ({ refNumber, screenshot, note }) => {
    setSubmitting(true);
    setApiError('');
    try {
      const formData = new FormData();
      formData.append('amount', resolvedAmount);
      if (refNumber)  formData.append('reference_number', refNumber);
      if (note)       formData.append('note', note);
      if (screenshot) formData.append('screenshot', screenshot);

      const res = await investmentAPI.manualDeposit(formData);
      setLastInvest(res.data.data);
      setShowBank(false);
      setSelected(null);
      setCustomAmt('');
      investmentAPI.getBalance().then((r) => setBalance(r.data.data)).catch(() => {});
    } catch (err) {
      setApiError(
        err.response?.data?.errors?.amount?.[0] ||
        err.response?.data?.message ||
        'Submission failed. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader title="Invest" subtitle="Grow your wealth with gold" />

      {/* Balance summary */}
      {balance && (
        <div className="mx-4 mb-4">
          <div
            className="rounded-3xl px-5 py-4"
            style={{ background: 'linear-gradient(135deg, #d4a017 0%, #f0c040 100%)' }}
          >
            <p className="text-xs text-amber-100 font-medium mb-1">Total Balance</p>
            <p className="text-2xl font-serif font-bold text-white">
              ₹{fmt(balance.total_balance)}
            </p>
            <div className="flex gap-4 mt-2">
              <div>
                <p className="text-[10px] text-amber-200">Deposited</p>
                <p className="text-sm font-semibold text-white">₹{fmt(balance.deposited_amount)}</p>
              </div>
              <div>
                <p className="text-[10px] text-amber-200">Profit</p>
                <p className="text-sm font-semibold text-white">₹{fmt(balance.total_profit)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs — hidden while showing bank details */}
      {!showBank && (
        <div
          className="flex gap-1 mx-4 mb-4 rounded-2xl p-1"
          style={{ background: 'rgba(255,255,255,0.08)' }}
        >
          {[
            { key: 'invest',  label: 'Invest Now' },
            { key: 'history', label: 'My History' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => { setTab(key); setLastInvest(null); setShowBank(false); }}
              className="flex-1 py-2.5 text-sm font-semibold rounded-xl capitalize transition-all"
              style={tab === key ? TAB_ACTIVE : TAB_INACTIVE}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="px-4 pb-6">

        {/* ── Bank details view (amount > 2500) ─────────────────────── */}
        <AnimatePresence mode="wait">
          {tab === 'invest' && showBank && (
            <BankDetailsView
              key="bank"
              amount={resolvedAmount}
              bankData={bankData}
              onBack={() => { setShowBank(false); setApiError(''); }}
              onConfirm={handleManualConfirm}
              submitting={submitting}
              error={apiError}
            />
          )}

          {/* ── Invest Tab ──────────────────────────────────────────── */}
          {tab === 'invest' && !showBank && (
            <motion.div
              key="invest"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex flex-col gap-4"
            >
              <AnimatePresence>
                {lastInvest && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Card>
                      <div className="flex flex-col items-center gap-3 py-2">
                        <div
                          className="w-14 h-14 rounded-full flex items-center justify-center"
                          style={{ background: 'rgba(5,150,105,0.18)' }}
                        >
                          <CheckCircle size={28} color="#6ee7b7" />
                        </div>
                        <div className="text-center">
                          <p className="font-serif font-bold text-white text-lg">
                            {lastInvest.razorpay_payment_id ? 'Payment Successful!' : 'Investment Submitted!'}
                          </p>
                          <p className="text-sm mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>
                            ₹{fmt(lastInvest.amount)} · <StatusBadge status={lastInvest.status} />
                          </p>
                          <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
                            Your investment is under review.
                          </p>
                        </div>
                        <button
                          onClick={() => setLastInvest(null)}
                          className="text-xs underline underline-offset-2"
                          style={{ color: '#f0c040' }}
                        >
                          Invest again
                        </button>
                      </div>
                    </Card>
                  </motion.div>
                )}
              </AnimatePresence>

              {!lastInvest && (
                <Card>
                  <div className="flex items-center gap-2 mb-5">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #d4a017, #f0c040)' }}
                    >
                      <TrendingUp size={16} color="white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">Select Investment Amount</p>
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
                        Choose a preset or enter custom amount
                      </p>
                    </div>
                  </div>

                  {apiError && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="mb-4 rounded-xl border px-4 py-3 text-sm"
                      style={{ background: 'rgba(239,68,68,0.15)', borderColor: 'rgba(239,68,68,0.3)', color: '#fca5a5' }}
                    >
                      {apiError}
                    </motion.div>
                  )}

                  {/* Preset chips */}
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {PRESET_AMOUNTS.map((amt) => (
                      <motion.button
                        key={amt}
                        whileTap={{ scale: 0.94 }}
                        onClick={() => { setSelected(amt); setCustomAmt(''); setApiError(''); }}
                        className="rounded-2xl py-3 text-center transition-all"
                        style={selected === amt ? CHIP_ACTIVE : CHIP_INACTIVE}
                      >
                        <p className="text-[10px] font-medium opacity-80">₹</p>
                        <p className="text-sm font-bold leading-tight">{fmt(amt)}</p>
                      </motion.button>
                    ))}

                    <motion.button
                      whileTap={{ scale: 0.94 }}
                      onClick={() => { setSelected('custom'); setApiError(''); }}
                      className="rounded-2xl py-3 text-center transition-all"
                      style={selected === 'custom' ? CHIP_ACTIVE : CHIP_INACTIVE}
                    >
                      <p className="text-[10px] font-medium opacity-80">₹</p>
                      <p className="text-sm font-bold leading-tight">Other</p>
                    </motion.button>
                  </div>

                  <AnimatePresence>
                    {selected === 'custom' && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden mb-3"
                      >
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: '#f0c040' }}>
                            <IndianRupee size={15} />
                          </span>
                          <input
                            type="number"
                            min="1"
                            placeholder="Enter amount in rupees"
                            value={customAmt}
                            onChange={(e) => { setCustomAmt(e.target.value); setApiError(''); }}
                            className="input-field pl-9 w-full"
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {resolvedAmount > 0 && (
                    <div
                      className="flex items-center justify-between rounded-2xl border px-4 py-3 mb-4"
                      style={{ background: 'rgba(212,160,23,0.1)', borderColor: 'rgba(212,160,23,0.3)' }}
                    >
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>You will invest</p>
                      <p className="text-base font-bold" style={{ color: '#f0c040' }}>
                        ₹{fmt(resolvedAmount)}
                      </p>
                    </div>
                  )}

                  <Button
                    fullWidth
                    loading={investing || loadingBank}
                    disabled={!resolvedAmount || resolvedAmount < 1}
                    onClick={handleInvest}
                  >
                    {isManual
                      ? (loadingBank ? 'Loading…' : `View Bank Details — ₹${resolvedAmount > 0 ? fmt(resolvedAmount) : '—'}`)
                      : (investing   ? 'Processing…' : `Pay ₹${resolvedAmount > 0 ? fmt(resolvedAmount) : '—'}`)
                    }
                  </Button>

                  {/* Payment method hint */}
                  <div className="flex items-center justify-center gap-1.5 mt-3">
                    <Building2 size={12} style={{ color: 'rgba(255,255,255,0.3)' }} />
                    <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
                      Bank / UPI transfer — admin will verify your payment
                    </p>
                  </div>
                </Card>
              )}
            </motion.div>
          )}

          {/* ── History Tab ─────────────────────────────────────────── */}
          {tab === 'history' && (
            <motion.div
              key="history"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex flex-col gap-3"
            >
              {loadingHistory ? (
                [1, 2, 3, 4].map((i) => (
                  <div key={i} className="skeleton h-16 rounded-2xl" />
                ))
              ) : history.length === 0 ? (
                <Card className="text-center py-10">
                  <TrendingUp size={32} className="mx-auto mb-2" style={{ color: 'rgba(255,255,255,0.2)' }} />
                  <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>No investment history yet</p>
                </Card>
              ) : (
                <>
                  {history.map((inv, idx) => (
                    <motion.div
                      key={inv.id || idx}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                    >
                      <Card className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ background: inv.type === 'profit' ? 'rgba(5,150,105,0.18)' : 'rgba(212,160,23,0.15)' }}
                        >
                          {inv.type === 'profit'
                            ? <ArrowUpCircle  size={20} color="#6ee7b7" />
                            : <ArrowDownCircle size={20} color="#f0c040" />
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white capitalize">
                            {inv.type === 'profit' ? 'Profit Credit' : 'Deposit'}
                          </p>
                          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{inv.date}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-bold text-white">₹{fmt(inv.amount)}</p>
                          <StatusBadge status={inv.status ?? 'pending'} />
                        </div>
                      </Card>
                    </motion.div>
                  ))}

                  {pagination && pagination.last_page > 1 && (
                    <div className="flex items-center justify-between pt-2">
                      <button
                        disabled={historyPage === 1}
                        onClick={() => loadHistory(historyPage - 1)}
                        className="text-xs font-semibold px-3 py-2"
                        style={{ color: historyPage === 1 ? 'rgba(255,255,255,0.2)' : '#f0c040' }}
                      >
                        ← Previous
                      </button>
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                        Page {historyPage} of {pagination.last_page}
                      </p>
                      <button
                        disabled={historyPage === pagination.last_page}
                        onClick={() => loadHistory(historyPage + 1)}
                        className="text-xs font-semibold px-3 py-2"
                        style={{ color: historyPage === pagination.last_page ? 'rgba(255,255,255,0.2)' : '#f0c040' }}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </div>
  );
}
